/**
 * LocationPortalPage — /wp/:pathOrId
 *
 * Universal location detail page. Handles any depth of the geographic
 * hierarchy:
 *   /wp/india                              → country page
 *   /wp/india/kerala                       → state page
 *   /wp/india/kerala/kottayam              → district page
 *   /wp/united-states/oregon/multnomah-county
 *   /wp/<uuid>                             → id-based lookup
 *
 * Reads `/api/wp/locations/{id}`. Handles both slug and UUID.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ChevronRight, MapPin, Building2, Loader2, Globe2 } from "lucide-react";
import { resolveSiteSlug, siteProfile } from "@/config/siteConfig";
import WorldPulseCommunity from "@/pages/WorldPulseCommunity";
import { useContextualPhotos, UnsplashCredit } from "@/hooks/useContextualPhoto";
import LocationPhotoStrip from "@/components/LocationPhotoStrip";

export default function LocationPortalPage() {
  const params = useParams();
  const slugPath = params["*"] || params.slug || "";
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const brand = siteProfile(resolveSiteSlug());
  const accent = brand.accent || "#4f46e5";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState({ loading: true, error: null, data: null });
      try {
        // Two lookup paths: (1) by id if it looks like a UUID,
        // (2) via search on slug tail as a fallback.
        const looksLikeUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(slugPath);
        let locId = slugPath;
        if (!looksLikeUuid) {
          const tail = slugPath.split("/").filter(Boolean).slice(-1)[0];
          const r = await axios.get(`${API}/wp/locations/search`, {
            params: { q: decodeURIComponent(tail || ""), limit: 5 }
          });
          // Prefer the hit whose slug matches the full path.
          const norm = slugPath.replace(/^\/+|\/+$/g, "");
          const exact = (r.data.hits || []).find(h => (h.slug || "").toLowerCase() === norm.toLowerCase());
          const first = exact || (r.data.hits || [])[0];
          if (!first) throw new Error("not-found");
          locId = first.id;
        }
        const r = await axios.get(`${API}/wp/locations/${locId}`);
        if (!cancelled) setState({ loading: false, error: null, data: r.data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || "error", data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [slugPath]);

  // Unsplash + lazy-seed hooks — MUST live above the early returns to
  // preserve React's stable hook call order.
  const location = state.data?.location;
  const parent   = state.data?.parent;
  const photoQuery = location
    ? [location.official_name, parent?.official_name, "landmarks landscape"]
        .filter(Boolean).join(" ")
    : null;
  // Single fetch — 3 photos powers both the hero backdrop AND the
  // 3-photo strip below. Zero wasted API calls.
  const photos = useContextualPhotos(photoQuery, { count: 3 });
  const heroPhoto = photos[0] || null;

  useEffect(() => {
    if (!location) return;
    // Record visit for the "Trending places" widget (fire-and-forget).
    axios.post(`${API}/wp/trending/visit?location_id=${location.id}`).catch(() => {});
    if (location.kind === "country") return; // countries seeded via Wikipedia
    axios.post(`${API}/wp/community/${location.id}/seed-lazy`)
      .catch(() => { /* silent — 4xx just means already seeded */ });
  }, [location?.id, location?.kind]);

  // External portal redirect — India and USA have their own dedicated
  // sites. Hitting /wp/india or /wp/united-states on WorldPulse pings
  // the visitor over to the flagship portal.
  useEffect(() => {
    if (!location) return;
    const external = ({
      IN: "https://indias.com",
      US: "https://usapulse.net",
    })[location.iso_country_code];
    if (external && location.kind === "country") {
      window.location.replace(external);
    }
  }, [location?.iso_country_code, location?.kind]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  }
  if (state.error || !state.data) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <div className="text-center">
          <p className="mb-2">Location not found.</p>
          <Link to="/" className="text-indigo-600 underline">Back home</Link>
        </div>
      </div>
    );
  }
  const { children, children_total } = state.data;

  // Build breadcrumb from the slug chain (efficient — no extra API call).
  const crumbs = (location.slug || "").split("/").filter(Boolean);
  const crumbLinks = crumbs.map((seg, i) => ({
    label: seg.replace(/-/g, " "),
    href: "/wp/" + crumbs.slice(0, i + 1).join("/"),
  }));

  const childrenByKind = {};
  for (const c of (children || [])) {
    (childrenByKind[c.kind] ||= []).push(c);
  }
  const kindOrder = ["state", "province", "region", "county", "district", "subdistrict", "city", "town", "village", "locality", "neighborhood"];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="wp-location-page">
      {/* Breadcrumb + header with Unsplash hero backdrop */}
      <div className="relative bg-white border-b overflow-hidden">
        {/* Contextual Unsplash photo — one per page, cached server-side */}
        {heroPhoto?.url && (
          <>
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${heroPhoto.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center 40%",
              }}
            />
            <div className="absolute inset-0" style={{
              background: `linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.85) 100%)`,
            }} />
            <div className="absolute bottom-1 right-3 z-10">
              <UnsplashCredit photo={heroPhoto} className="!text-slate-500" />
            </div>
          </>
        )}
        <div className="relative max-w-6xl mx-auto px-4 py-6">
          <nav className="flex items-center gap-1 text-xs text-slate-500 mb-3 flex-wrap" data-testid="wp-breadcrumbs">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/wp" className="hover:text-slate-900">World</Link>
            {crumbLinks.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                {i === crumbLinks.length - 1
                  ? <span className="capitalize text-slate-900">{c.label}</span>
                  : <Link to={c.href} className="capitalize hover:text-slate-900">{c.label}</Link>}
              </span>
            ))}
          </nav>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="text-3xl font-semibold text-slate-900">{location.official_name}</h1>
            <span className="text-xs uppercase tracking-wider text-slate-400 px-2 py-0.5 rounded" style={{ background: `${accent}15`, color: accent }}>
              {location.kind}
            </span>
            {location.iso_country_code && (
              <span className="text-xs text-slate-500">ISO: {location.iso_country_code}{location.iso_subdivision_code ? ` · ${location.iso_subdivision_code}` : ""}</span>
            )}
          </div>
          {parent && (
            <div className="mt-2 text-sm text-slate-500">
              Part of <Link to={`/wp/${parent.slug || parent.id}`} className="text-slate-700 hover:underline">{parent.official_name}</Link>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Photo strip — 3 contextual Unsplash photos below the hero.
              Reuses the same fetch as the hero — zero extra API calls. */}
          {photos.length > 0 && <LocationPhotoStrip photos={photos} count={3} />}

          {/* Children — the primary content until Phase 3 lights up news/weather */}
          {kindOrder.map(kind => {
            const list = childrenByKind[kind];
            if (!list?.length) return null;
            return (
              <section key={kind} className="bg-white border rounded-lg p-5" data-testid={`wp-children-${kind}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <h2 className="text-lg font-semibold text-slate-900 capitalize">{kind}s</h2>
                  <span className="text-sm text-slate-500">{list.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {list.slice(0, 60).map(c => (
                    <Link
                      key={c.id}
                      to={`/wp/${c.slug || c.id}`}
                      className="text-sm text-slate-700 hover:text-slate-900 hover:underline truncate py-1"
                      data-testid={`wp-child-${c.id}`}
                    >
                      {c.official_name}
                    </Link>
                  ))}
                </div>
                {list.length > 60 && (
                  <div className="text-xs text-slate-400 mt-3">Showing 60 of {list.length}. Full list coming in Phase 2.</div>
                )}
              </section>
            );
          })}

          {children_total === 0 && (
            <section className="bg-white border rounded-lg p-8 text-center text-slate-500">
              This is a leaf location. Community layer, news feed, and events coming in Phase 3+.
            </section>
          )}

          {/* WorldPulse Community — country pages use ISO route; state/city
              pages use the location-id route with lazy AI seeding. */}
          {location.kind === "country" && location.iso_country_code && (
            <WorldPulseCommunity iso={location.iso_country_code} accent={accent} />
          )}
          {location.kind !== "country" && (
            <WorldPulseCommunity locationId={location.id} accent={accent} />
          )}

          {/* Phase 3+ placeholder tiles */}
          <section className="grid md:grid-cols-2 gap-3">
            {[
              ["News", "Phase 3", "Local aggregation via FeedHub."],
              ["Weather", "Phase 3", "Provider-independent WeatherHub."],
              ["Events", "Phase 4", "Community + partner event streams."],
              ["Businesses", "Phase 5", "Local directory + claims."],
            ].map(([title, phase, desc]) => (
              <div key={title} className="border rounded-lg p-4 bg-white opacity-70">
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{phase}</div>
                <div className="text-base font-medium text-slate-900 mb-1">{title}</div>
                <div className="text-sm text-slate-500">{desc}</div>
              </div>
            ))}
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-5" data-testid="wp-facts">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Facts</h3>
            <dl className="text-sm space-y-2">
              <Fact k="Type" v={location.kind} />
              <Fact k="Admin level" v={location.admin_level ?? "—"} />
              {location.iso_country_code && <Fact k="Country ISO" v={location.iso_country_code} />}
              {location.iso_subdivision_code && <Fact k="Subdivision ISO" v={location.iso_subdivision_code} />}
              {location.fips_code && <Fact k="FIPS" v={location.fips_code} />}
              {location.latitude != null && <Fact k="Coordinates" v={`${location.latitude?.toFixed(3)}, ${location.longitude?.toFixed(3)}`} />}
              {location.wikidata_id && <Fact k="Wikidata" v={<a href={`https://www.wikidata.org/wiki/${location.wikidata_id}`} className="text-indigo-600 hover:underline" target="_blank" rel="noreferrer">{location.wikidata_id}</a>} />}
            </dl>
          </div>
          <div className="bg-white border rounded-lg p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-1.5"><Globe2 className="w-4 h-4" style={{ color: accent }} />Sister-site views</h3>
            <p className="text-xs text-slate-500 mb-3">Every sister site references this canonical location ID.</p>
            <div className="text-xs font-mono text-slate-500 break-all bg-slate-50 rounded p-2">{location.id}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Fact({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500 text-xs uppercase tracking-wider pt-0.5">{k}</dt>
      <dd className="text-slate-900 text-right">{v}</dd>
    </div>
  );
}
